import React, { useState } from 'react';
import MainLayout from '../../layout/MainLayout';
import DashboardLayout from "../../layout/DashboardLayout";
import StepOneContent from '../../components/AssessmentsAndTests/StepOne/StepOneContent';
import StepTwoContent from '../../components/AssessmentsAndTests/StepTwo/StepTwoContent';
import StepThreeContent from '../../components/AssessmentsAndTests/StepThree/StepThreeContent';
import StepSixHeader from '../../components/AssessmentsAndTests/StepSix/StepSixHeader';
import StepSixContent from '../../components/AssessmentsAndTests/StepSix/StepSixContent';
import StepFourHeader from '../../components/AssessmentsAndTests/StepFour/StepFourHeader';
import { useParams } from 'react-router-dom';
import { axiosPrivate } from '../../api/axios';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import { v4 as uuidv4 } from 'uuid';

const AssessmentsOverview = () => {
  const initialStep = parseInt(new URLSearchParams(window.location.search).get('step') ?? "0");

  const [step, setStep] = useState<number>(initialStep);
  const [progress, setProgress] = useState<number>(0);
  const { id } = useParams();
  const resultId = new URLSearchParams(window.location.search).get('resultId');
  const [assessment, setAssessment] = useState<any>({});
  const [questions, setQuestions] = useState<any[]>([]);
  const [timeTaken, setTimeTaken] = useState<any[]>([]);
  const [userLocation, setUserLocation] = useState<string>('');
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [unansweredQuestionIds, setUnansweredQuestionIds] = useState([]);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = React.useState<any>(
    questions.map(() => [])
  );
  const [answeredFlags, setAnsweredFlags] = useState(
    Array(questions.length).fill(false)
  );
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const stepThreeRef = React.useRef<HTMLDivElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const resultIdScreenshot = uuidv4();

  React.useEffect(() => {
    (async () => {
      const response = await axiosPrivate.get(`/assessment/user/details/${id}`);
      setAssessment(response.data?.data?.assessment || null);
      const suffledQuestions = shuffle(response.data?.data?.questions || []);
      setQuestions(suffledQuestions);
    })();
  }, [id]);

  // Shuffle Function (No change)
  const shuffle = (array: any[]): any[] => {
    return array.sort(() => Math.random() - 0.5);
  };

  React.useEffect(() => {
    const detectWindowsKey = (e: KeyboardEvent) => {
      // Detect Meta Key (Windows Key) or any combination with it
      if (e.metaKey || e.key === "Meta" || e.key === "OS") {
        console.warn("Windows Key (Meta Key) Pressed!");
        toast.error("Windows Key is not allowed during the assessment.");

        // Optional: Force exit or redirect user (Uncomment if needed)
        window.location.href = '/assessments';
      }
    };

    // Add event listener for all keyboard events
    window.addEventListener("keydown", detectWindowsKey, true);
    window.addEventListener("keyup", detectWindowsKey, true);

    return () => {
      // Remove event listener on cleanup
      window.removeEventListener("keydown", detectWindowsKey, true);
      window.removeEventListener("keyup", detectWindowsKey, true);
    };
  }, []);

  React.useEffect(() => {
    const disableKeyboard = (e: KeyboardEvent) => {
      // Prevent all keys including Windows (Meta), Alt, Ctrl, Shift
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const disableRightClick = (e: MouseEvent) => {
      e.preventDefault();
      toast.warning("Right-click is disabled on this page.");
    };

    // Add keyboard and mouse event listeners
    window.addEventListener("keydown", disableKeyboard, true);
    window.addEventListener("keyup", disableKeyboard, true);
    window.addEventListener("keypress", disableKeyboard, true);
    window.addEventListener("contextmenu", disableRightClick);

    return () => {
      // Remove event listeners on cleanup
      window.removeEventListener("keydown", disableKeyboard, true);
      window.removeEventListener("keyup", disableKeyboard, true);
      window.removeEventListener("keypress", disableKeyboard, true);
      window.removeEventListener("contextmenu", disableRightClick);
    };
  }, []);

  React.useEffect(() => {
    const progressValues = [20, 45, 60, 80, 100];
    setProgress(progressValues[step] || 0);
  }, [step]);

  React.useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  React.useEffect(() => {
    let screenshotInterval: NodeJS.Timeout;

    if (step === 2) {
      screenshotInterval = setInterval(() => {
        if (!loading) {
          takeScreenshotAndUpload(); // Directly upload screenshot
        }
      }, 30000); // 1 minute interval
    }

    return () => {
      clearInterval(screenshotInterval);
    };
  }, [step, loading, isSubmitting]);

  // Get User Location with Error Handling
  const getUserLocation = async () => {
    try {
      if (!navigator.geolocation) {
        toast.error("Geolocation is not supported by this browser.");
        return false;
      }
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation(`${latitude}, ${longitude}`);
            resolve(true);
          },
          () => {
            toast.error("Location permission is required to continue.");
            resolve(false);
          }
        );
      });
    } catch (error) {
      toast.error("Failed to access location.");
      return false;
    }
  };

  // Enable Camera (Full-Screen Mode) with Error Handling
  const enableFullScreen = async () => {
    let consoleDetected = false;

    // Ultimate Console Detection with Performance Test
    const detectConsoleOpen = setInterval(() => {
      const start = performance.now();
      debugger; // This will slow down if console is open
      const end = performance.now();

      if (end - start > 100) { // If the console is open, it will take longer
        consoleDetected = true;
        clearInterval(detectConsoleOpen);
        alert("Console is open. Please close it to continue.");
        window.location.href = '/assessments';
      }
    }, 500);

    try {
      // If the video is already playing, do nothing
      if (videoRef.current && videoRef.current.srcObject) {
        console.log("Camera already enabled.");
        clearInterval(detectConsoleOpen);
        return true;
      }

      // Access camera and set stream
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        // Check if the video already has a stream
        if (videoRef.current.srcObject) {
          const existingStream = videoRef.current.srcObject as MediaStream;
          existingStream.getTracks().forEach((track) => track.stop()); // Stop existing stream
          videoRef.current.srcObject = null; // Clear the source
        }

        videoRef.current.srcObject = stream;

        // Only play if not already playing
        if (videoRef.current.paused || videoRef.current.readyState <= 2) {
          await videoRef.current.play().catch((err) => {
            console.warn("Play request interrupted:", err);
          });
        }
        console.log("Camera access granted and playing.");
      } else {
        console.error("Video element not found.");
        toast.error("Video element not found for camera.");
        clearInterval(detectConsoleOpen);
        return false;
      }

      // Attempt to enter full-screen mode (optional)
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
          console.log("Fullscreen enabled.");
        }
      } catch (err) {
        console.warn("Fullscreen request failed:", err);
      }

      clearInterval(detectConsoleOpen);
      return true;
    } catch (err) {
      console.error("Error accessing camera:", err);
      toast.error("Camera access is required to continue.");
      clearInterval(detectConsoleOpen);
      return false;
    }
  };

  // Handle Next Step Logic with Permissions Check
  const nextStep = async () => {
    try {
      if (step === 0) {
        const locationGranted = await getUserLocation();
        if (!locationGranted) {
          toast.error("Location permission is required to continue.");
          return;
        }
      }

      if (step === 1) {
        const cameraGranted = await enableFullScreen();
        if (!cameraGranted) {
          toast.error("Camera permission is required to continue.");
          return;
        }
      }

      setStep(step + 1);
    } catch (error) {
      console.error("Unexpected error in nextStep:", error);
      toast.error("Unexpected error occurred. Please try again.");
    }
  };

  // Handle Previous Step
  const prevStep = () => setStep(step > 0 ? step - 1 : 0);

  const fetchLocationInfo = async () => {
    try {
      if (!userLocation) return { latLng: null, country: null, state: null, district: null };

      const [lat, lng] = userLocation.split(',').map(val => val.trim());

      const response = await fetch(
        `https://api.opencagedata.com/geocode/v1/json?q=${lat}+${lng}&key=189ec282ca004c509f068ded75b01743`
      );

      const data = await response.json();
      const result = data.results[0];

      return {
        latLng: userLocation,
        country: result.components.country || null,
        state: result.components.state || null,
        district:
          result.components.county ||
          result.components.city ||
          result.components.town ||
          result.components.village ||
          null
      };
    } catch (error) {
      console.error("Reverse geocoding failed:", error);
      return {
        latLng: userLocation || null,
        country: null,
        state: null,
        district: null
      };
    }
  };

  const handleSubmitAssessment = async () => {
    setLoading(true);
    setIsSubmitting(true);

    try {
      const locationData = await fetchLocationInfo();

      const payload = {
        selectedAnswers: selectedAnswers.map((ans: any) => ({
          questionId: ans.questionId,
          answerId: ans.answerId,
        })),
        timeTaken,
        screenshots,
        location: locationData
      };
 
      const response = await axiosPrivate.post(`/assessment-result/${id}`, payload);
      const resultId = response.data?.result?._id;

      setLoading(false);
      window.location.href = `/assessments/result?resultId=${resultId}&retakeUrl=${window.location.pathname}`;
    } catch (err: any) {
      setLoading(false);
      setIsSubmitting(false);
      toast.error("Failed to submit assessment.");
      console.error("Submission error:", err);
    }
  };

  // Full-Screen Exit Detection
  const handleFullScreenChange = () => {
    if (!document.fullscreenElement) {
      setCancelling(true);
      setTimeout(() => {
        window.location.href = '/assessments';
      }, 3000);
    }
  };

  const handleTimeReamain = (reaminTime: any) => {
    setTimeTaken(reaminTime);
    if (reaminTime == '0h 00m 00s') {
      setCancelling(true);
      setTimeout(() => {
        window.location.href = '/assessments';
      }, 3000);
    }
  }

  const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
    const question = questions[questionIndex];
    const answer = question.answers[answerIndex];

    const newSelectedAnswers = [...selectedAnswers];

    // Remove existing entry for this questionId (if any)
    const existingIndex = newSelectedAnswers.findIndex(
      (entry) => entry.questionId === question._id
    );

    if (existingIndex !== -1) {
      // Deselect if same answer is selected again (optional toggle behavior)
      if (newSelectedAnswers[existingIndex].answerId === answer._id) {
        newSelectedAnswers.splice(existingIndex, 1);
      } else {
        newSelectedAnswers[existingIndex] = {
          questionId: question._id,
          answerId: answer._id,
        };
      }
    } else {
      newSelectedAnswers.push({
        questionId: question._id,
        answerId: answer._id,
      });
    }

    setSelectedAnswers(newSelectedAnswers);
    const updatedFlags = [...answeredFlags];
    updatedFlags[questionIndex] = true;
    setAnsweredFlags(updatedFlags);
    setAnsweredCount((prevCount) => prevCount + 1);
  };

  const retake = () => {
    (window as Window).location = window.location.pathname;
  };

  const takeScreenshotAndUpload = async () => {
    if (!videoRef.current) {
      console.error("Video element not found for screenshot.");
      return;
    }
    try {
      // @ts-ignore
      // Capture the entire page (including video and background)
      const canvas = await html2canvas(document.documentElement, {
        useCORS: true, // Allow CORS images
        allowTaint: false, // Prevent cross-origin tainting
        backgroundColor: null,
        scale: 2, // Higher quality
      });

      // Convert the captured canvas to an image (Base64)
      const image = canvas.toDataURL('image/png');
      await uploadScreenshotToS3(image); // Directly upload to S3

      console.log("Screenshot captured and uploaded successfully.");
    } catch (error) {
      // console.error("Error taking screenshot:", error);
    }
  };

  // ✅ Function to Upload Screenshot Directly to S3
  const uploadScreenshotToS3 = async (base64Image: string) => {
    try {
      // Request the pre-signed URL from the backend
      const response = await axiosPrivate.post(`/assessment/${id}/upload-screenshot-url`, {
        resultIdScreenshot, // Using the generated UUID as the folder ID
      });
      const presignedUrl = response.data.presignedUrl;

      // Convert Base64 image to Blob
      const base64Data = base64Image.replace(/^data:image\/\w+;base64,/, "");
      const byteString = atob(base64Data);
      const byteArray = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        byteArray[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: "image/png" });

      // Upload the Blob using the pre-signed URL
      const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        body: blob,
        headers: {
          "Content-Type": "image/png",
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("S3 Upload failed");
      }

      // Store the uploaded URL
      const uploadedUrl = presignedUrl.split("?")[0];
      setScreenshots((prev) => [...prev, uploadedUrl]);
      console.log("Screenshot uploaded:", uploadedUrl);
    } catch (error) {
      console.error("S3 Upload Error:", error);
    }
  };

  return (
    <>
      {cancelling && (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-screen z-50 overflow-hidden bg-gray-400 opacity-75 flex flex-col items-center justify-center">
          <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4"></div>
          <h2 className="text-center text-white text-xl font-semibold">
            Cancelling your assessment...
          </h2>
          <p className="w-1/3 text-center text-white">
            This is due to violating the terms.
          </p>
        </div>
      )}
      <video
        ref={videoRef}
        className="h-[150px] w-[200px] fixed bottom-0 right-0"
      />
      {(() => {
        switch (step) {
          case 0:
            return (
              <DashboardLayout>
                <StepOneContent assessmentInfo={assessment} nextStep={nextStep} step={step} progress={progress} />
              </DashboardLayout>
            );
          case 1:
            return (
              <DashboardLayout>
                <StepTwoContent
                  headerProgress={progress}
                  nextStep={() => {
                    nextStep();
                    enableFullScreen();
                  }}
                  prevStep={prevStep}
                  assessmentId={id}
                  getUserLocation={getUserLocation}
                  setAcceptTerms={setAcceptTerms}
                  acceptTerms={acceptTerms}
                  step={step}
                  enableFullScreen={enableFullScreen}
                />
              </DashboardLayout>
            );
          case 2:
            return (
              <MainLayout
                headerProgress={progress}
                headerContent={<StepFourHeader assessmentInfo={{
                  ...assessment,
                  duration: Number(assessment?.duration) || 0
                }} handleTimeReamain={handleTimeReamain} />}
              >
                <div ref={stepThreeRef}>
                  <StepThreeContent
                    prevStep={prevStep}
                    nextStep={handleSubmitAssessment}
                    questions={questions}
                    selectedAnswers={selectedAnswers}
                    handleAnswerSelect={handleAnswerSelect}
                    loading={loading}
                    unansweredQuestionIds={unansweredQuestionIds}
                    handleTimeReamain={handleTimeReamain}
                    assessmentInfo={{
                      ...assessment,
                      duration: Number(assessment?.duration) || 0
                    }}
                  />

                </div>
              </MainLayout>
            );
          case 3:
            return (
              <MainLayout
                headerProgress={100}
                headerContent={<StepSixHeader retake={retake} />}
              >
                <StepSixContent id={resultId || ''} />
              </MainLayout>
            );

          default:
            break;
        }
      })()}
    </>
  );
};

export default AssessmentsOverview;
